import { pickedFile } from '../../utils/pickedFile.js'
import MediaCaptureField from '../common/MediaCaptureField.js'
import React, { useState, useEffect, useCallback } from 'react';
import SelectField from '../common/SelectField.js';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js"
import Alert from '../common/Alert.js';
import DocumentModal from '../common/DocumentModal.js';
import axiosInstance from '../../config/axios.js';
import userService from '../../services/UserService.js';
import companyService from '../../services/CompanyService.js';
import { locationHubService } from '../../services/locationHubService.js';
import { formatLocationDropdownLabel, sortLocationsAsc } from '../../utils/locationDropdownLabel.js';
import { Users } from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import StateSelectField from '../common/StateSelectField.js';
import AgentPartySelect from './AgentPartySelect.js';
import AgentInternalFlags from './AgentInternalFlags.js';
import {
  PARTY_LINKED_ROLES,
  filterCreatableRoles,
  ensureRoleInList,
} from '../../utils/userCreatableRoles.js';
import { canEditUserRecord } from '../../utils/companyAccess.js';
import { isContractPartyAdmin, isHubAdmin } from '../../utils/networkMode.js';


const SALARY_TYPE_OPTIONS = [
  { value: 'fixed_monthly', label: 'Fixed Monthly Salary' },
  { value: 'daily_wage', label: 'Daily Wages' },
];

const getSalaryAmountLabel = (salaryType) =>
  salaryType === 'daily_wage' ? 'Daily Wage (₹)' : 'Fixed Monthly Salary (₹)';

const getSalaryAmountPlaceholder = (salaryType) =>
  salaryType === 'daily_wage' ? 'Enter daily wage amount' : 'Enter monthly salary';

const EditUser = () => {
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    email: '',
    contact: '',
    city: '',
    state: '',
    location: '',
    dob: '',
    document_type: '',
    document_number: '',
    role_id: '',
    status: 'active',
    salary_type: 'fixed_monthly',
    monthly_salary: '',
    booking_commission: '',
    delivery_commission: '',
    company_id: '',
    company_name: '',
    company_string_id: '',
    party_id: '',
    party_name: '',
    commission_enabled: false,
    is_pickup_man: false,
    tds_percentage: '0',
  });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [documentAttachment, setDocumentAttachment] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [existingProfilePhoto, setExistingProfilePhoto] = useState(null);
  const [drivingLicenseAttachment, setDrivingLicenseAttachment] = useState(null);
  const [drivingLicensePreview, setDrivingLicensePreview] = useState(null);
  const [existingDrivingLicense, setExistingDrivingLicense] = useState(null);
  const [hasExistingLicense, setHasExistingLicense] = useState(false);
  const [existingDocument, setExistingDocument] = useState(null);
  const [hasExistingDocument, setHasExistingDocument] = useState(false);
  const [roles, setRoles] = useState([]);
  const [roleLocked, setRoleLocked] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [locationHubs, setLocationHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    name:            (v) => validators.required(v, 'Name') || validators.textOnly(v, 'Name'),
    email:           (v) => validators.required(v, 'Email') || validators.email(v),
    contact:         (v) => validators.required(v, 'Contact') || validators.contact(v),
    document_number: (v) => validators.required(v, 'Document number') || validators.alphanumeric(v, 'Document number'),
    monthly_salary:  (v) => validators.number(v, { min: 0, label: 'Salary' }),
  };

  const documentTypes = ['aadhar', 'pan', 'driving_license', 'voter_id', 'passport'];
  const userStatuses = ['active', 'inactive'];

  // Check permissions
  const canUseEditPage = ['System Admin', 'Admin', 'Accountant', 'Contract Party Admin', 'Hub Admin'].includes(currentUser?.role);
  const isSystemAdmin = currentUser?.role === 'System Admin';
  const isHubAdminUser = isHubAdmin(currentUser);
  const isContractPartyAdminUser = isContractPartyAdmin(currentUser);
  const canEditPassword = ['System Admin', 'Admin', 'Accountant'].includes(currentUser?.role);
  const roleFieldLocked =
    (roleLocked && !isSystemAdmin) || isContractPartyAdminUser || isHubAdminUser;

  const fetchLocationHubs = useCallback(async (companyId) => {
    if (!companyId) {
      setLocationHubs([]);
      return;
    }

    try {
      const response = await locationHubService.getAllHubs(1, 1000, {
        all: 'true',
        company_id: companyId,
      });

      let hubs = response.data.hubs || [];
      // Hub Admin may only keep / assign hubs under their own hub scope
      if (isHubAdminUser && currentUser?.location_hub_id != null) {
        hubs = hubs.filter(
          (h) => String(h.id) === String(currentUser.location_hub_id)
        );
      }
      setLocationHubs(hubs);
    } catch (err) {
      console.error('Failed to load location hubs', err);
      setLocationHubs([]);
    }
  }, [isHubAdminUser, currentUser?.location_hub_id]);

  useEffect(() => {
    if (!canUseEditPage) {
      Alert.error('You do not have permission to edit users');
      navigate('/users');
      return;
    }

    fetchUserAndRoles();
    // Load once per userId; permission helpers are stable for this page session
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Fetch location hubs when company changes
  useEffect(() => {
    if (!formData.company_id && !formData.company_string_id) {
      setLocationHubs([]);
      return;
    }

    if (isSystemAdmin && companies.length > 0 && formData.company_id) {
      const selectedCompany = companies.find(
        (c) => String(c.id) === String(formData.company_id)
      );
      if (selectedCompany) {
        fetchLocationHubs(selectedCompany.company_id);
        return;
      }
    }

    fetchLocationHubs(formData.company_string_id || formData.company_id);
  }, [formData.company_id, formData.company_string_id, companies, isSystemAdmin, fetchLocationHubs]);

  const fetchUserAndRoles = async () => {
    try {
      const [userRes, rolesRes] = await Promise.all([
        userService.getById(userId),
        axiosInstance.get('/roles'),
      ]);
      
      const userData = userRes.data || userRes;
      const allRolesFromApi = rolesRes.data?.data || [];

      if (!canEditUserRecord(userData, currentUser)) {
        Alert.error('You do not have access to this user');
        navigate('/users');
        return;
      }

      setFormData({
        username: userData.username || '',
        name: userData.name || '',
        email: userData.email || '',
        contact: userData.contact || '',
        city: userData.city || '',
        state: userData.state || '',
        location: userData.location || '',
        dob: userData.dob ? String(userData.dob).split('T')[0] : '',
        document_type: userData.document_type || '',
        document_number: userData.document_number || '',
        role_id: userData.role_id != null ? String(userData.role_id) : '',
        status: userData.status || 'active',
        salary_type: userData.salary_type || 'fixed_monthly',
        monthly_salary: userData.monthly_salary != null ? String(userData.monthly_salary) : '',
        booking_commission: userData.booking_commission != null ? String(userData.booking_commission) : (userData.shipment_commission != null ? String(userData.shipment_commission) : ''),
        delivery_commission: userData.delivery_commission != null ? String(userData.delivery_commission) : (userData.shipment_commission != null ? String(userData.shipment_commission) : ''),
        company_id: userData.company_internal_id != null
          ? String(userData.company_internal_id)
          : '',
        company_name: userData.company_name || '',
        company_string_id: userData.company_id || '',
        party_id: userData.party_id != null ? String(userData.party_id) : '',
        party_name: userData.party_name || '',
        location_hub_id: userData.location_hub_id != null ? String(userData.location_hub_id) : '',
        commission_enabled: Boolean(userData.commission_enabled),
        is_pickup_man: Boolean(userData.is_pickup_man),
        tds_percentage: userData.tds_percentage != null ? String(userData.tds_percentage) : '0',
      });
      setRoleLocked(Boolean(userData.role_locked));
      
      // Filter roles based on current user's role
      let editableRoles = filterCreatableRoles(allRolesFromApi, currentUser);

      // Always include the user's current role so the dropdown shows the correct name
      editableRoles = ensureRoleInList(editableRoles, userData.role_id, allRolesFromApi);
      setRoles(editableRoles);

      // Fetch companies before hubs so company dropdown + branch list stay in sync
      if (isSystemAdmin) {
        const companiesRes = await companyService.getActiveCompanies();
        setCompanies(companiesRes.data || []);
      }
      
      // Set existing profile photo (direct Spaces URL from API)
      if (userData.profile_photo_url) {
        setExistingProfilePhoto(userData.profile_photo_url);
      }

      // Set existing document (direct Spaces URL + existence flag from API)
      setExistingDocument(userData.document_attachment_url || null);
      setHasExistingDocument(userData.has_document_attachment || false);

      // Set existing driving license (direct Spaces URL + existence flag from API)
      setExistingDrivingLicense(userData.driving_license_attachment_url || null);
      setHasExistingLicense(userData.has_driving_license_attachment || false);
      
    } catch (err) {
      Alert.error('Failed to load user data');
      console.error(err);
      navigate('/users');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    if (name === 'contact') filtered = inputFilters.digits10(value);
    else if (name === 'name') filtered = inputFilters.lettersOnly(value);
    else if (name === 'monthly_salary') filtered = inputFilters.amount(value);
    else if (name === 'document_number') filtered = inputFilters.alphanumeric(value);

    if (name === 'city') {
      const hub = locationHubs.find((h) => h.location === value);
      setFormData((prev) => ({
        ...prev,
        city: value,
        state: hub?.state || prev.state,
        location_hub_id: hub?.id != null ? String(hub.id) : '',
      }));
      return;
    }

    if (name === 'company_id') {
      const selectedCompany = companies.find((c) => String(c.id) === String(value));
      setFormData((prev) => ({
        ...prev,
        company_id: value,
        company_string_id: selectedCompany?.company_id || prev.company_string_id,
        city: '',
        state: '',
      }));
      return;
    }

    if (name === 'role_id') {
      const role = roles.find((r) => String(r.id) === String(filtered));
      setFormData((prev) => ({
        ...prev,
        role_id: filtered,
        party_id: PARTY_LINKED_ROLES.includes(role?.role_name) ? prev.party_id : '',
        party_name: PARTY_LINKED_ROLES.includes(role?.role_name) ? prev.party_name : '',
        commission_enabled: role?.role_name === 'Agent' ? prev.commission_enabled : false,
        is_pickup_man: role?.role_name === 'Agent' ? prev.is_pickup_man : false,
        tds_percentage: role?.role_name === 'Agent' ? prev.tds_percentage : '0',
      }));
      return;
    }

    if (name === 'party_id') {
      setFormData((prev) => ({
        ...prev,
        party_id: filtered,
        party_name: filtered ? prev.party_name : '',
        commission_enabled: filtered ? true : prev.commission_enabled,
      }));
      return;
    }

    if (e.target?.type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: e.target.checked }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: filtered }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }
  };

  const handleProfilePhotoChange = (e) => {
    const file = pickedFile(e);
    if (file) {
      if (file.size > 1024 * 1024) {
        Alert.error('Profile photo must be less than 1MB');
        return;
      }
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDocumentAttachmentChange = (e) => {
    const file = pickedFile(e);
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        Alert.error('Document must be less than 2MB');
        return;
      }
      setDocumentAttachment(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setDocumentPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeProfilePhoto = () => {
    setProfilePhoto(null);
    setProfilePhotoPreview(null);
    const fileInput = document.getElementById('profile-photo-input');
    if (fileInput) fileInput.value = '';
  };

  const removeExistingProfilePhoto = () => {
    setExistingProfilePhoto(null);
  };

  const removeDocumentAttachment = () => {
    setDocumentAttachment(null);
    setDocumentPreview(null);
    const fileInput = document.getElementById('document-attachment-input');
    if (fileInput) fileInput.value = '';
  };

  const handleDrivingLicenseChange = (e) => {
    const file = pickedFile(e);
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        Alert.error('Driving license must be less than 2MB');
        return;
      }
      setDrivingLicenseAttachment(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setDrivingLicensePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeDrivingLicense = () => {
    setDrivingLicenseAttachment(null);
    setDrivingLicensePreview(null);
    const fileInput = document.getElementById('driving-license-input');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData, validationRules)) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }

    if (canEditPassword && newPassword.trim()) {
      const passwordError = validators.password(newPassword);
      if (passwordError) {
        Alert.error(passwordError);
        return;
      }
    }

    setSubmitting(true);

    try {
      const payload = { ...formData };
      delete payload.username;
      delete payload.company_name;
      delete payload.company_string_id;
      delete payload.party_name;

      // Backend: only System Admin may change company — omit for everyone else
      if (!isSystemAdmin) {
        delete payload.company_id;
      }

      const selectedRole = roles.find((r) => String(r.id) === String(payload.role_id));
      const selectedRoleName = selectedRole?.role_name;

      // Agent: always send party_id. Empty string = own-company (backend clears the old party).
      // Omitting party_id would leave Star Graphics / previous contract party in place.
      if (selectedRoleName === 'Agent') {
        payload.party_id = payload.party_id ? String(payload.party_id) : '';
        payload.commission_enabled = Boolean(formData.commission_enabled);
        payload.is_pickup_man = Boolean(formData.is_pickup_man);
        payload.tds_percentage = formData.tds_percentage === '' ? 0 : Number(formData.tds_percentage);
      } else {
        delete payload.commission_enabled;
        delete payload.is_pickup_man;
        delete payload.tds_percentage;
        if (!PARTY_LINKED_ROLES.includes(selectedRoleName)) {
          delete payload.party_id;
        }
      }

      if (!payload.location_hub_id) {
        delete payload.location_hub_id;
      } else if (
        isHubAdminUser &&
        currentUser?.location_hub_id != null &&
        String(payload.location_hub_id) !== String(currentUser.location_hub_id)
      ) {
        // Keep hub assignment inside Hub Admin's scope
        payload.location_hub_id = String(currentUser.location_hub_id);
      }

      if (!payload.dob?.trim()) {
        delete payload.dob;
      } else {
        payload.dob = payload.dob.split('T')[0];
      }

      // Don't send role_id when the field is locked (Admin self-edit, Hub Admin, CPA)
      if (roleFieldLocked) {
        delete payload.role_id;
      }
      if (isContractPartyAdminUser) {
        delete payload.party_id;
      }

      // Password reset: System Admin / Admin / Accountant only; never send empty
      if (canEditPassword && newPassword.trim()) {
        payload.password = newPassword.trim();
      } else {
        delete payload.password;
      }

      await userService.update(userId, payload, profilePhoto, documentAttachment, drivingLicenseAttachment);
      Alert.success('User updated successfully!');
      setTimeout(() => navigate('/users'), 1500);
    } catch (err) {
      Alert.modal(err.response?.data?.error || 'Failed to update user', 'Cannot update user');
    } finally {
      setSubmitting(false);
    }
  };

  const openDocumentModal = () => {
    setIsDocModalOpen(true);
  };

  const closeDocumentModal = () => {
    setIsDocModalOpen(false);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading user data..." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-primary flex items-center gap-2">
                    <Users className="w-6 h-6 sm:w-7 sm:h-7" />
                    Edit User
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">Update user information</p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
                <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                  
                  {/* Company Field - Only editable for System Admin */}
                  {isSystemAdmin ? (
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Company Information
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-6">
                        <div className="sm:col-span-2">
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Company <span className="text-danger font-bold">*</span>
                          </label>
                          <SelectField variant="form"
                            name="company_id"
                            value={formData.company_id}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                            required
                          >
                            <option value="">Select Company</option>
                            {companies.map((company) => (
                              <option key={company.company_id} value={company.id}>
                                {company.company_name} ({company.company_id})
                              </option>
                            ))}
                          </SelectField>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Company Information
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-6">
                        <div className="sm:col-span-2">
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Company
                          </label>
                          <input
                            type="text"
                            value={formData.company_name || ''}
                            disabled
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl bg-gray-100 text-gray-600 text-sm sm:text-base cursor-not-allowed"
                          />
                          <p className="text-xs text-gray-500 mt-1">Company cannot be changed</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section 1: Basic Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Basic Information
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Username
                        </label>
                        <input
                          type="text"
                          value={formData.username || ''}
                          disabled
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl bg-gray-100 text-gray-600 text-sm sm:text-base cursor-not-allowed"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Full Name <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Enter full name"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.name ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.name} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Date of Birth
                        </label>
                        <input
                          type="date"
                          name="dob"
                          value={formData.dob || ''}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div className="sm:col-span-1">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Profile Photo
                        </label>
                        
                        {existingProfilePhoto && !profilePhotoPreview && (
                          <div className="mb-3">
                            <div className="relative inline-block">
                              <img
                                src={existingProfilePhoto}
                                alt="Current Profile"
                                className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover border-2 border-gray-300"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  setExistingProfilePhoto(null);
                                }}
                              />
                              <button
                                type="button"
                                onClick={removeExistingProfilePhoto}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                title="Remove existing photo"
                              >
                                ×
                              </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">Current Photo</p>
                          </div>
                        )}

                        <MediaCaptureField mode="photo" onPick={handleProfilePhotoChange} />
                        <p className="text-xs text-gray-600 mt-1">
                          {existingProfilePhoto ? 'Upload new photo to replace (Max 1MB)' : 'Max 1MB'}
                        </p>
                        {profilePhotoPreview && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700 mb-2">New Photo Preview:</p>
                            <div className="relative inline-block">
                              <img
                                src={profilePhotoPreview}
                                alt="New Profile Preview"
                                className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover border-2 border-gray-300"
                              />
                              <button
                                type="button"
                                onClick={removeProfilePhoto}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                title="Remove new photo"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Contact Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Contact Information
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Email <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter email"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.email ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.email} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Contact <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="tel"
                          name="contact"
                          value={formData.contact}
                          onChange={handleChange}
                          placeholder="Enter 10-digit number"
                          maxLength={10}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.contact ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.contact} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Branch <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        >
                          <option value="">Select Branch</option>
                          {formData.city &&
                            !locationHubs.some((hub) => hub.location === formData.city) && (
                              <option value={formData.city}>{formData.city}</option>
                            )}
                          {sortLocationsAsc(locationHubs).map((hub) => (
                            <option key={hub.hub_id} value={hub.location}>
                              {formatLocationDropdownLabel(hub)}
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      <StateSelectField
                        label="State"
                        value={formData.state}
                        onChange={(state) => setFormData((prev) => ({ ...prev, state }))}
                      />

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Location <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="location"
                          value={formData.location}
                          onChange={handleChange}
                          placeholder="Enter location/address"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Document Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Document Information
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Document Type <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="document_type"
                          value={formData.document_type}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        >
                          {documentTypes.map((type) => (
                            <option key={type} value={type}>
                              {type.replace(/_/g, ' ').toUpperCase()}
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Document Number <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="document_number"
                          value={formData.document_number}
                          onChange={handleChange}
                          placeholder="Enter document number"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.document_number ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.document_number} />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Document Attachment
                        </label>
                        
                        {hasExistingDocument && !documentPreview && (
                          <Button
                            size="sm"
                            onClick={openDocumentModal}
                            className="mb-3"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            View Existing Document
                          </Button>
                        )}
                        
                        <MediaCaptureField mode="photo" onPick={handleDocumentAttachmentChange} />
                        <p className="text-xs text-gray-600 mt-1">
                          {hasExistingDocument ? 'Upload new document to replace existing one (Max 2MB)' : 'Upload scanned copy of the document (Max 2MB)'}
                        </p>
                        
                        {documentPreview && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700 mb-2">New Document Preview:</p>
                            <div className="relative inline-block">
                              <img
                                src={documentPreview}
                                alt="Document Preview"
                                className="w-32 h-32 sm:w-40 sm:h-40 object-cover border border-gray-300 rounded-md"
                              />
                              <button
                                type="button"
                                onClick={removeDocumentAttachment}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                title="Remove document"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Driving License Section - Only for Driver role */}
                  {formData.role_id && roles.find(r => r.id === parseInt(formData.role_id))?.role_name === 'Driver' && (
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400 mt-6">
                        Driving License
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-6">
                        <div className="sm:col-span-2">
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Driving License Attachment
                          </label>
                          
                          {hasExistingLicense && !drivingLicensePreview && (
                            <Button
                              size="sm"
                              onClick={() => window.open(existingDrivingLicense, '_blank')}
                              className="mb-3"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              View Existing License
                            </Button>
                          )}
                          
                          <MediaCaptureField mode="photo" onPick={handleDrivingLicenseChange} />
                          <p className="text-xs text-gray-600 mt-1">
                            {hasExistingLicense ? 'Upload new license to replace existing one (Max 2MB)' : 'Upload driving license (Max 2MB)'}
                          </p>
                          
                          {drivingLicensePreview && (
                            <div className="mt-3">
                              <p className="text-sm font-medium text-gray-700 mb-2">New License Preview:</p>
                              <div className="relative inline-block">
                                <img
                                  src={drivingLicensePreview}
                                  alt="License Preview"
                                  className="w-32 h-32 sm:w-40 sm:h-40 object-cover border border-gray-300 rounded-md"
                                />
                                <button
                                  type="button"
                                  onClick={removeDrivingLicense}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                  title="Remove license"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section 4: Role and Status */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Role and Status
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Role <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="role_id"
                          value={formData.role_id || ''}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                          disabled={roleFieldLocked}
                        >
                          <option value="">Select Role</option>
                          {roles.map((role) => (
                            <option key={role.id} value={String(role.id)}>
                              {role.role_name}
                            </option>
                          ))}
                        </SelectField>
                        {roleFieldLocked && (
                          <p className="text-xs text-orange-600 mt-1">
                            {isHubAdminUser || isContractPartyAdminUser
                              ? 'You cannot change role for this user.'
                              : 'Role is locked for this user.'}
                          </p>
                        )}
                      </div>

                      {formData.role_id && PARTY_LINKED_ROLES.includes(
                        roles.find((r) => String(r.id) === String(formData.role_id))?.role_name
                      ) && !isContractPartyAdminUser && (
                        <AgentPartySelect
                          value={formData.party_id}
                          onChange={handleChange}
                          companyId={formData.company_id}
                          roleName={
                            roles.find((r) => String(r.id) === String(formData.role_id))?.role_name
                          }
                        />
                      )}

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Salary Type
                        </label>
                        <SelectField variant="form"
                          name="salary_type"
                          value={formData.salary_type}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        >
                          {SALARY_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          {getSalaryAmountLabel(formData.salary_type)}
                        </label>
                        <input
                          type="text"
                          name="monthly_salary"
                          value={formData.monthly_salary !== '' && formData.monthly_salary != null ? formData.monthly_salary : ''}
                          onChange={handleChange}
                          placeholder={getSalaryAmountPlaceholder(formData.salary_type)}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.monthly_salary ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.monthly_salary} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Booking Commission (%)
                        </label>
                        <input
                          type="number"
                          name="booking_commission"
                          value={formData.booking_commission ?? ''}
                          onChange={handleChange}
                          placeholder="0"
                          min="0"
                          max="100"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Delivery Commission (%)
                        </label>
                        <input
                          type="number"
                          name="delivery_commission"
                          value={formData.delivery_commission ?? ''}
                          onChange={handleChange}
                          placeholder="0"
                          min="0"
                          max="100"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Status
                        </label>
                        <SelectField variant="form"
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        >
                          {userStatuses.map((status) => (
                            <option key={status} value={status}>
                              {status.toUpperCase()}
                            </option>
                          ))}
                        </SelectField>
                      </div>
                      <AgentInternalFlags
                        visible={
                          roles.find((r) => String(r.id) === String(formData.role_id))?.role_name === 'Agent'
                          && (
                            currentUser?.role === 'System Admin'
                              ? (formData.company_string_id
                                ? companies.find((c) => String(c.company_id) === String(formData.company_string_id) || String(c.id) === String(formData.company_id))?.network_mode !== 'EXTERNAL'
                                : currentUser?.network_mode !== 'EXTERNAL')
                              : currentUser?.network_mode !== 'EXTERNAL'
                          )
                        }
                        formData={formData}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {canEditPassword && (
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Reset Password
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-6">
                        <div className="sm:col-span-2">
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            New Password
                          </label>
                          <div className="relative">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              name="new_password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="Enter new password"
                              autoComplete="new-password"
                              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword((prev) => !prev)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-sm"
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                              {showPassword ? 'Hide' : 'Show'}
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Leave blank to keep the current password. Existing password is never shown.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6 border-t">
                    <Button type="submit" loading={submitting}>
                      {submitting ? 'Updating...' : 'Update User'}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/users')}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>

      {/* Document Modal */}
      {hasExistingDocument && (
        <DocumentModal
          isOpen={isDocModalOpen}
          onClose={closeDocumentModal}
          documentUrl={existingDocument}
          documentType={formData.document_type}
        />
      )}
    </DashboardLayout>
  );
};

export default EditUser;